import { useCallback, useEffect, useRef, useState } from "react";
import { dayHeading, hasNewNotes, LATEST_NOTES, loadNotesSeen, RELEASE_NOTES, saveNotesSeen, type ReleaseDay } from "../game/release-notes";
import { SECONDARY } from "./buttons";
import { padEdges, type PadRead } from "./lobby-nav";
import { PANEL, SCRIM, TITLE } from "./theme";

// What's new (#159): the release notes, on Pause > What's new (a row per
// day, so the stick scrolls them) and behind a link on the start screen.

/** Whether there are notes the player hasn't seen, and a way to mark them seen. */
export function useNotesSeen(): [boolean, () => void] {
  const [seen, setSeen] = useState(loadNotesSeen);
  const markSeen = useCallback(() => {
    if (!hasNewNotes(seen)) return;
    saveNotesSeen();
    setSeen(LATEST_NOTES);
  }, [seen]);
  return [hasNewNotes(seen), markSeen];
}

/** The dot on What's new while there are notes the player hasn't seen. */
export function NewDot() {
  return (
    <span className="ml-2 inline-block size-2.5 rounded-full bg-danger align-middle" role="img" aria-label="New" />
  );
}

/** One day's notes as bullets. */
export function DayNotes({ day }: { day: ReleaseDay }) {
  return (
    <ul className="mt-1 list-disc pl-5 text-sm font-semibold normal-case not-italic">
      {day.notes.map((n) => (
        <li key={n.text}>{n.text}</li>
      ))}
    </ul>
  );
}

/** Stick or d-pad held up or down this frame (-1, 0 or 1), to scroll the list. */
function scrollDir(now: PadRead): number {
  const ay = now.axes[1] ?? 0;
  return now.buttons[12] || ay < -0.5 ? -1 : now.buttons[13] || ay > 0.5 ? 1 : 0;
}

/** Polls the first pad while the dialog's up: B or Circle closes it, the stick scrolls it. */
function usePadClose(list: React.RefObject<HTMLElement | null>, onClose: () => void): void {
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    let prev: PadRead | null = null;
    let frame = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      let pad: Gamepad | null = null;
      try {
        pad = [...navigator.getGamepads()].find((p): p is Gamepad => !!p) ?? null;
      } catch {
        return; // a sandbox that refuses the Gamepad API
      }
      if (!pad) return;
      const now: PadRead = { buttons: pad.buttons.map((b) => b.pressed), axes: [...pad.axes] };
      // The first read only sets the baseline, like the lobby's: a button already down isn't a press.
      const edges = prev ? padEdges(prev, now) : [];
      prev = now;
      if (edges.includes("back")) close.current();
      else if (list.current) list.current.scrollTop += scrollDir(now) * PAD_SCROLL;
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [list]);
}

/** How far the list scrolls each frame the stick is held (px). */
const PAD_SCROLL = 12;

/**
 * The whole list over the start screen, newest first. Its header (the title
 * and an X) stays put while the list scrolls under it (#185), and it closes
 * on the X, the Close at the bottom, a tap outside it, Esc, or the pad's B
 * or Circle. Keys pressed while it's open stay with it, so none drops in.
 */
export function WhatsNewDialog({ onClose }: { onClose: () => void }) {
  const list = useRef<HTMLDivElement>(null);
  usePadClose(list, onClose);
  useEffect(() => {
    // Capture on window runs before the lobby's "any key drops in" and the game's own keys.
    const key = (e: KeyboardEvent) => {
      e.stopPropagation();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", key, true);
    // The list takes focus, so the arrow keys and Page Up/Down scroll it.
    list.current?.focus({ preventScroll: true });
    return () => window.removeEventListener("keydown", key, true);
  }, [onClose]);
  return (
    <div
      className={`absolute inset-0 grid place-items-center ${SCRIM} p-4`}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label="What's new"
        className={`${PANEL} flex max-h-[calc(100dvh-2rem)] w-[min(90vw,28rem)] flex-col overflow-hidden`}
      >
        <div className="flex shrink-0 items-center gap-2 border-b-2 border-line py-3 pl-5 pr-3">
          {/* The X's width on the left too, so the title stays centred. */}
          <span aria-hidden="true" className="size-10 shrink-0" />
          <h2 className={`${TITLE} flex-1 text-center text-3xl`}>What's new</h2>
          <button
            type="button"
            aria-label="Close What's new"
            onClick={onClose}
            className="grid size-10 shrink-0 place-items-center rounded-full border-2 border-line text-center text-lg font-black hover:bg-surface-soft focus-visible:outline-4 focus-visible:outline-focus"
          >
            ✕
          </button>
        </div>
        <div ref={list} data-news-list tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto p-5 pt-3 outline-none">
          {RELEASE_NOTES.map((day) => (
            <section key={day.date} className="mb-3">
              <h3 className="text-sm font-black uppercase italic tracking-widest">{dayHeading(day.date)}</h3>
              <DayNotes day={day} />
            </section>
          ))}
          <button type="button" onClick={onClose} className={`${SECONDARY} mt-2 w-full px-4 py-2 text-center text-lg`}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
